/** @jsx jsx */
/** @jsxRuntime classic */

import { jsx } from '@emotion/react';
import React, { useCallback, useMemo, useState } from 'react';
import Icon from '../icons/Icon';
import useTheme from '../style/useTheme';
import Typography from '../Typography/Typography';
import { noop } from '../utils';
import InteractiveDiv from '../InteractiveDiv/InteractiveDiv';
import { SxProp } from '../style/sxProp';

export type Props = {
  label: string;
  content: string[];
  onToggle?: (open: boolean) => void;
  sx?: SxProp;
  role?: React.AriaRole;
  contentId?: string;
};

const Collapser: React.FC<Props> = ({
  label,
  content,
  onToggle = noop,
  sx,
  role,
  contentId,
}) => {
  const { theme, resolveColor, resolveCSS } = useTheme();
  const [open, setOpen] = useState(false);
  const [bodyElement, setBodyElement] = useState<HTMLElement>();
  const [bodyHeight, setBodyHeight] = useState<number>(0);

  const onBodyElementChange = useCallback(
    (node: HTMLElement | null) => {
      if (!node) return;
      setBodyElement(node);
      setBodyHeight(node.clientHeight);
    },
    [setBodyElement, setBodyHeight]
  );

  const body = useMemo(() => {
    if (!content.length) {
      return null;
    }
    if (content.length === 1) {
      return (
        <Typography variant="caption" id={contentId}>
          {content[0]}
        </Typography>
      );
    }

    return (
      <ul
        css={{
          listStyleType: 'disc',
          padding: 'unset',
        }}
        id={contentId}
      >
        {content.map((item, index) => (
          // Use both the content string as well as its index to form a proper key. If we didn't use the index, it's
          // possible that with duplicate content strings, we could re-render them out of order. We need to suppress
          // this line because eslint is not smart enough to detect that we're not *only* using the index as the key.
          // eslint-disable-next-line react/no-array-index-key
          <li key={`${item}-${index}`}>
            <Typography variant="caption">{item}</Typography>
          </li>
        ))}
      </ul>
    );
  }, [content, contentId]);

  return (
    <div
      css={resolveCSS(sx)}
      data-testid="Collapser"
      className="origami-Collapser"
      role={role}
    >
      <InteractiveDiv
        css={{
          display: 'flex',
          marginTop: '12px',
          cursor: 'pointer',
          alignItems: 'center',
          '&:hover > div': {
            backgroundColor: resolveColor({
              type: 'component',
              value:
                theme.components.collapser.default.iconBackgroundOnHoverColor,
            }),
            borderRadius: '4px',
          },
        }}
        role="region"
        aria-label={`${label} content`}
        data-testid="Collapser-header"
        onClick={() => {
          if (onToggle) {
            onToggle(!open);
          }
          setOpen(!open);
        }}
      >
        <div
          css={{
            display: 'flex',
            ...(open && { transform: 'rotate(90deg)' }),
            transition: `transform ${theme.animations.duration.short}ms ${theme.animations.easing.sharp}`,
          }}
        >
          <Icon icon="chevronRight" size={24} />
        </div>
        <Typography
          sx={{
            marginLeft: '8px',
            lineHeight: 'normal',
            fontWeight: 700,
          }}
          variant="caption"
        >
          {label}
        </Typography>
      </InteractiveDiv>
      <div
        ref={onBodyElementChange}
        data-testid="Collapser-body"
        css={{
          ...(bodyElement
            ? { maxHeight: open ? bodyHeight : 0 }
            : { visibility: 'hidden' }),
          transition: `max-height ${theme.animations.duration.short}ms ${theme.animations.easing.sharp}`,
          marginTop: 0,
          paddingLeft: '32px',
          overflow: 'hidden',
        }}
        style={
          // Once we have the HTML element and have calculated it's height, we toggle max height
          // between 0 and it's natural height to get a smooth animation since you can't animate
          // on height: 100%. Before we have the HTML element, we need the element to be at it's
          // natural height but not visible. So we set visibility hidden during that phase.
          bodyElement
            ? { maxHeight: open ? bodyHeight : 0 }
            : { visibility: 'hidden' }
        }
        aria-hidden={!open}
        className="origami-Collapser__body infoCaptionText"
      >
        {body}
      </div>
    </div>
  );
};

export default Collapser;
